import { AlertTriangle, EyeOff } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/modules/shared/ui/components/card"
import {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableRow,
  TableHead,
  TableCell,
} from "@/modules/shared/ui/components/table"
import { Avatar, AvatarFallback } from "@/modules/shared/ui/components/avatar"
import { Alert, AlertDescription } from "@/modules/shared/ui/components/alert"
import { cn } from "@/lib/utils"
import { usePanelScorecards } from "@/modules/recruitment/hooks/useScorecards"
import {
  SCORE_STYLES,
  RECOMMENDATION_LABELS,
  RECOMMENDATION_STYLES,
  STALE_BADGE_STYLE,
  getInitials,
} from "@/modules/recruitment/applications/ui/components/scorecard-styles"

interface ScorecardPanelComparisonProps {
  organizationId: string
  applicationId: string
}

export function ScorecardPanelComparison({ organizationId, applicationId }: ScorecardPanelComparisonProps) {
  const { data, isLoading } = usePanelScorecards(organizationId, applicationId)

  if (isLoading) {
    return <p className="text-sm text-muted-foreground py-6">Loading panel scorecards&hellip;</p>
  }

  if (!data?.canViewPanel) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Panel comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <EyeOff className="size-4" />
            <AlertDescription>
              Submit your own scorecard to see how the rest of the panel scored this candidate.
              Scores stay hidden until then so nobody anchors on someone else&apos;s rating.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    )
  }

  const panelists = [data.myScorecard, ...data.otherScorecards]
    .filter((s): s is NonNullable<typeof s> => s != null)
    .map((s) => ({
      id: s.submittedByUserId,
      name: s.submittedByName,
      recommendation: s.recommendation,
      weightedTotal: s.weightedTotal,
      isMe: s.submittedByUserId === data.myScorecard?.submittedByUserId,
      isStale: s.isStale,
    }))

  if (panelists.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Panel comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No submitted scorecards yet.</p>
        </CardContent>
      </Card>
    )
  }

  const divergingRows = data.criteriaComparison.filter((row) => row.diverges)

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle>Panel comparison</CardTitle>
        <span className="text-xs px-2.5 py-1 rounded-full border bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-900/50">
          {panelists.length} submitted
        </span>
      </CardHeader>

      <CardContent className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {panelists.map((panelist) => (
            <div key={panelist.id} className="border border-border rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Avatar className="size-7">
                  <AvatarFallback className="text-[11px] font-medium">
                    {getInitials(panelist.name)}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm truncate">
                  {panelist.name}
                  {panelist.isMe && <span className="text-muted-foreground"> (you)</span>}
                </span>
              </div>
              {panelist.isStale && (
                <span
                  className={cn(
                    "inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border w-fit",
                    STALE_BADGE_STYLE,
                  )}
                  title="Submitted before the scorecard template was last updated"
                >
                  <AlertTriangle className="size-3" />
                  Outdated
                </span>
              )}
              <div className="flex items-center justify-between gap-2">
                {panelist.recommendation ? (
                  <span
                    className={cn(
                      "text-xs px-2 py-0.5 rounded-full border",
                      RECOMMENDATION_STYLES[panelist.recommendation],
                    )}
                  >
                    {RECOMMENDATION_LABELS[panelist.recommendation]}
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">No recommendation</span>
                )}
                {panelist.weightedTotal != null && (
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {panelist.weightedTotal.toFixed(1)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-48">Criterion</TableHead>
                {panelists.map((panelist) => (
                  <TableHead key={panelist.id} className="text-center">
                    {getInitials(panelist.name)}
                  </TableHead>
                ))}
                <TableHead className="text-right">Avg</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {data.criteriaComparison.map((row) => {
                const scoresByUser = new Map(row.scores.map((s) => [s.panelistUserId, s.score]))
                const values = row.scores
                  .map((s) => s.score)
                  .filter((s): s is number => s != null)
                const avg =
                  values.length > 0
                    ? (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1)
                    : "—"

                return (
                  <TableRow
                    key={row.criterionId}
                    className={cn(row.diverges && "bg-amber-50/60 dark:bg-amber-900/10")}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {row.diverges && (
                          <AlertTriangle className="size-4 text-amber-600 dark:text-amber-500 shrink-0" />
                        )}
                        <span>{row.label}</span>
                        <span className="text-xs text-muted-foreground">w{row.weight}</span>
                      </div>
                    </TableCell>

                    {panelists.map((panelist) => {
                      const score = scoresByUser.get(panelist.id)
                      return (
                        <TableCell key={panelist.id} className="text-center">
                          {score != null ? (
                            <span
                              className={cn(
                                "inline-flex h-7 w-7 items-center justify-center rounded-md border text-xs font-medium",
                                SCORE_STYLES[score],
                              )}
                            >
                              {score}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      )
                    })}

                    <TableCell className="text-right tabular-nums">{avg}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>

            <TableFooter>
              <TableRow>
                <TableCell className="font-medium">Weighted total</TableCell>
                {panelists.map((panelist) => (
                  <TableCell key={panelist.id} className="text-center tabular-nums font-medium">
                    {panelist.weightedTotal != null ? panelist.weightedTotal.toFixed(1) : "—"}
                  </TableCell>
                ))}
                <TableCell />
              </TableRow>
            </TableFooter>
          </Table>
        </div>

        {divergingRows.length > 0 && (
          <Alert className="border-amber-200 dark:border-amber-900/50 bg-amber-50/40 dark:bg-amber-900/10">
            <AlertTriangle className="size-4 text-amber-600 dark:text-amber-500" />
            <AlertDescription>
              The panel disagrees on{" "}
              <span className="font-medium">
                {divergingRows.map((row) => row.label).join(", ")}
              </span>
              . Worth resolving in the debrief before a decision.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  )
}
